import { useGridApiRef } from '@mui/x-data-grid-pro';
import type { InvoiceOverview } from '@vy/api-definition';
import { CurrencyExchangeRates, INVOICE_PDF_TYPES, PERMISSIONS } from '@vy/types';
import { isEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasPermissions } from '../../../../services/render-with-permissions';
import { InvoicesPageContent } from '../../../invoices/components';
import { useInvoicesActions } from '../../../invoices/hooks/use-invoices-actions';
import { useInvoicesData } from '../../../invoices/hooks/use-invoices-data';
import { useFilteredSyncSelectedInvoices } from '../../../invoices/hooks/use-selected-invoices-sync-filter';
import type {
  InvoicesActions,
  InvoicesDisabledStates,
  InvoicesPageData,
  InvoicesSyncDialogs,
  InvoicesTableConfig,
} from '../../../invoices/types/invoices-page-types';

type CustomerInvoicesPageContentContainerProps = {
  customerId: string;
  invoices: InvoiceOverview[];
  invoicesCount: number;
  loadingInvoices?: boolean;
  exchangeRates?: CurrencyExchangeRates;
  page?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

export const CustomerInvoicesPageContentContainer: React.FC<CustomerInvoicesPageContentContainerProps> = ({
  customerId,
  invoices,
  invoicesCount,
  loadingInvoices = false,
  exchangeRates,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const navigate = useNavigate();
  const [addSyncInvoicesDialogOpen, setAddSyncInvoicesDialogOpen] = useState<boolean>(false);
  const [addSyncInvoicesAsSalesOrderDialogOpen, setAddSyncInvoicesAsSalesOrderDialogOpen] =
    useState<boolean>(false);

  const apiRef = useGridApiRef();

  const {
    selectedInvoices,
    setSelectedInvoices,
    connectedIntegrations,
    resetInvoicesQuery,
    hasUsageEventsReportConfiguration,
  } = useInvoicesData();

  const filteredApprovedSelectedInvoices = useFilteredSyncSelectedInvoices(selectedInvoices);

  const actions = useInvoicesActions({
    selectedInvoices,
    apiRef,
    onSuccess: resetInvoicesQuery,
  });

  const handleSyncToNetSuiteOpen = () => {
    setAddSyncInvoicesDialogOpen(true);
  };

  const handleSyncToNetSuiteClose = () => {
    setAddSyncInvoicesDialogOpen(false);
  };

  const handleSyncToNetSuite = async () => {
    if (isNotNullOrUndefined(filteredApprovedSelectedInvoices)) {
      await actions.handleSyncToNetSuite(filteredApprovedSelectedInvoices);
    }
  };

  const toggleSyncToNetSuiteSalesOrderDialog = (open: boolean) => {
    setAddSyncInvoicesAsSalesOrderDialogOpen(open);
  };

  const handleSyncToNetSuiteSalesOrder = async () => {
    if (isNotNullOrUndefined(filteredApprovedSelectedInvoices)) {
      await actions.handleSyncToNetSuiteSalesOrder(filteredApprovedSelectedInvoices);
    }
  };

  const data: InvoicesPageData = {
    invoices,
    invoicesCount,
    selectedInvoices,
    loadingInvoices,
    connectedIntegrations,
    exchangeRates,
    hasUsageEventsReportConfiguration,
  };

  const tableConfig: InvoicesTableConfig = {
    apiRef,
    onInvoicesSelect: setSelectedInvoices,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
  };

  const actionsObj: InvoicesActions = {
    onApproveInvoices: actions.handleApproveSelectedInvoices,
    onExportSelectedInvoices: actions.handleExportSelectedInvoices,
    onDownloadAsPdf: () => actions.handleDownloadInvoicesPdfs(INVOICE_PDF_TYPES.Light),
    onDownloadAsPdfStatement: () => actions.handleDownloadInvoicesPdfs(INVOICE_PDF_TYPES.Statement),
    onUpdateInvoicesBillingStatus: actions.handleUpdateInvoicesBillingStatus,
    onSyncToStripe: actions.handleSyncToStripe,
    onSyncToQuickBooks: actions.handleSyncToQuickBooks,
    onSyncToMorning: actions.handleSyncToMorning,
    onSyncToMarketplace: actions.handleSyncToMarketplace,
    onSyncToXero: actions.handleSyncToXero,
    onSyncToPaddle: actions.handleSyncToPaddle,
    onDownloadUsageEventsReport: actions.handleDownloadUsageEventsReports,
  };

  const syncDialogs: InvoicesSyncDialogs = {
    invoice: {
      open: addSyncInvoicesDialogOpen,
      filteredApprovedInvoices: filteredApprovedSelectedInvoices,
      onOpen: handleSyncToNetSuiteOpen,
      onClose: handleSyncToNetSuiteClose,
      onSync: handleSyncToNetSuite,
    },
    salesOrder: {
      open: addSyncInvoicesAsSalesOrderDialogOpen,
      onOpen: () => toggleSyncToNetSuiteSalesOrderDialog(true),
      onClose: () => toggleSyncToNetSuiteSalesOrderDialog(false),
      onSync: handleSyncToNetSuiteSalesOrder,
    },
  };

  const disabledStates: InvoicesDisabledStates = {
    syncToNetSuiteDisabled: isEmptyArray(filteredApprovedSelectedInvoices),
    actionsDisabled: isEmptyArray(selectedInvoices),
  };

  const canCreateInvoice = hasPermissions([PERMISSIONS.INVOICE_CREATE]);
  const onCreateInvoice = canCreateInvoice
    ? () => navigate(`/invoices/create?customerId=${customerId}`)
    : undefined;

  return (
    <InvoicesPageContent
      data={data}
      tableConfig={tableConfig}
      actions={actionsObj}
      syncDialogs={syncDialogs}
      disabledStates={disabledStates}
      onCreateInvoice={onCreateInvoice}
      hideDownload
      disableSorting
    />
  );
};
